import { Component } from "react"
import { RouteComponentProps } from "@reach/router"
import { Maybe } from "@flockcover/flock-svc-core-types"
import { DefaultFallbackComponent, FallbackComponentProps } from "./DefaultFallbackComponent"

type ErrorInfo = Readonly<{
  componentStack: string
}>

export type Props = Readonly<{
  FallbackComponent?: React.FC<FallbackComponentProps>
  onError?: (error: Error, componentStack: string) => void
  debug?: boolean
  path?: RouteComponentProps
}>

type State = Readonly<{
  error: Maybe<Error>
  info: Maybe<ErrorInfo>
}>

export class ErrorBoundary extends Component<React.PropsWithChildren<Props>, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      error: null,
      info: null,
    }
  }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const { onError, debug } = this.props

    if (typeof onError === `function`) {
      try {
        onError.call(this, error, info?.componentStack ?? ``)
      } catch (ignoredError) {} // eslint-disable-line no-empty
    }
    if (debug) {
      console.error(`From Error Boundary: `, error) // eslint-disable-line no-console
    }
  }

  render() {
    const { children, debug, FallbackComponent = DefaultFallbackComponent } = this.props

    const { error, info } = this.state

    return error ? (
      <FallbackComponent debug={debug} error={error} componentStack={info?.componentStack ?? ``} />
    ) : (
      children
    )
  }
}
